import { useEffect, useState } from 'react'
import { useAuth } from './auth/AuthContext'
import Login from './screens/Login'
import Dashboard from './screens/Dashboard'
import Proposals from './screens/Proposals'
import NewProposal from './screens/NewProposal'
import ProposalDetail from './screens/ProposalDetail'
import ContractDetail from './screens/ContractDetail'
import Sequences from './screens/Sequences'
import NewSequence from './screens/NewSequence'
import SequenceDetail from './screens/SequenceDetail'
import Prospect from './screens/Prospect'
import TopBar from './components/TopBar'
import { colors } from './theme'

export type Route =
  | { name: 'dashboard' }
  | { name: 'proposals' }
  | { name: 'new' }
  | { name: 'detail'; id: string }
  | { name: 'contract'; id: string }
  | { name: 'sequences' }
  | { name: 'sequence-new' }
  | { name: 'sequence-detail'; id: string }
  | { name: 'prospect' }

// Hash-based router — keeps the bundle thin and Cloudflare Pages happy
// without a separate _redirects file.
function parseHash(): Route {
  const h = window.location.hash.replace(/^#\/?/, '')
  if (h.startsWith('sequence/')) {
    return {
      name: 'sequence-detail',
      id: decodeURIComponent(h.slice('sequence/'.length)),
    }
  }
  if (h === 'sequences/new') return { name: 'sequence-new' }
  if (h === 'sequences') return { name: 'sequences' }
  if (h === 'prospect') return { name: 'prospect' }
  if (h.startsWith('contract/')) {
    return {
      name: 'contract',
      id: decodeURIComponent(h.slice('contract/'.length)),
    }
  }
  if (h.startsWith('detail/')) {
    return { name: 'detail', id: decodeURIComponent(h.slice('detail/'.length)) }
  }
  if (h === 'new') return { name: 'new' }
  if (h === 'proposals') return { name: 'proposals' }
  // Default landing is now the dashboard. Older bookmarks at #/proposals
  // still route correctly (matched above); empty hash maps here.
  return { name: 'dashboard' }
}

export function navigate(route: Route) {
  let target = '#/dashboard'
  switch (route.name) {
    case 'dashboard':
      target = '#/dashboard'
      break
    case 'proposals':
      target = '#/proposals'
      break
    case 'new':
      target = '#/new'
      break
    case 'detail':
      target = `#/detail/${encodeURIComponent(route.id)}`
      break
    case 'contract':
      target = `#/contract/${encodeURIComponent(route.id)}`
      break
    case 'sequences':
      target = '#/sequences'
      break
    case 'sequence-new':
      target = '#/sequences/new'
      break
    case 'sequence-detail':
      target = `#/sequence/${encodeURIComponent(route.id)}`
      break
    case 'prospect':
      target = '#/prospect'
      break
  }
  if (window.location.hash !== target) window.location.hash = target
}

export default function App() {
  const { user } = useAuth()
  const [route, setRoute] = useState<Route>(() => parseHash())

  useEffect(() => {
    const onHash = () => setRoute(parseHash())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  if (!user) return <Login />

  return (
    <div style={{ minHeight: '100vh', background: colors.bg, color: colors.black }}>
      <TopBar route={route} />
      <main
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '32px 24px 64px',
        }}
      >
        {route.name === 'dashboard' && <Dashboard />}
        {route.name === 'proposals' && <Proposals />}
        {route.name === 'new' && <NewProposal />}
        {route.name === 'detail' && <ProposalDetail id={route.id} />}
        {route.name === 'contract' && <ContractDetail id={route.id} />}
        {route.name === 'sequences' && <Sequences />}
        {route.name === 'sequence-new' && <NewSequence />}
        {route.name === 'sequence-detail' && <SequenceDetail id={route.id} />}
        {route.name === 'prospect' && <Prospect />}
      </main>
    </div>
  )
}
